import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { useSendNotification } from "./use-send-notification";
import { isSmsAvailable, willSkipForPref } from "./notification-channels";
import { resolveResultToast, resolveSendErrorMessage } from "./notification-result-message";
import type { AdminNotificationResult, NotificationChannel } from "./notification-types";
import type { UserDetail } from "./types";

interface SendNotificationDialogProps {
  readonly user: UserDetail | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Called once a send returns `SENT`; the dialog has already closed. */
  readonly onSent: (result: AdminNotificationResult) => void;
}

const CHANNELS: readonly NotificationChannel[] = ["PUSH", "SMS", "EMAIL"];

export function SendNotificationDialog({
  user,
  open,
  onOpenChange,
  onSent,
}: SendNotificationDialogProps) {
  const { t } = useTranslation();
  const [channel, setChannel] = useState<NotificationChannel>("EMAIL");
  const [body, setBody] = useState("");
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [bypassPreferences, setBypassPreferences] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminNotificationResult | null>(null);
  const mutation = useSendNotification();

  useEffect(() => {
    if (open) {
      setChannel("EMAIL");
      setBody("");
      setTitle("");
      setSubject("");
      setBypassPreferences(false);
      setError(null);
      setResult(null);
    }
  }, [open]);

  if (!user) return null;

  const smsAvailable = isSmsAvailable(user);
  const prefs = user.notificationReadiness.preferences;
  const willSkip = willSkipForPref(channel, prefs, bypassPreferences);
  const showPushNoDevice = channel === "PUSH" && !user.notificationReadiness.hasActivePushDevice;

  const canSubmit =
    body.trim().length > 0 && !mutation.isPending && !(channel === "SMS" && !smsAvailable);

  // Any edit invalidates the previous outcome so stale feedback never
  // lingers next to changed input.
  const clearFeedback = () => {
    setError(null);
    setResult(null);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    clearFeedback();
    mutation.mutate(
      {
        userId: user.id,
        channel,
        body: body.trim(),
        title: channel === "PUSH" ? title.trim() || undefined : undefined,
        subject: channel === "EMAIL" ? subject.trim() || undefined : undefined,
        bypassPreferences: bypassPreferences || undefined,
      },
      {
        onSuccess: (res) => {
          if (res.status === "SENT") {
            onOpenChange(false);
            onSent(res);
            return;
          }
          // SKIPPED_* / FAILED: keep the dialog open so the operator can
          // adjust (e.g. tick "ignore preferences") and retry.
          setResult(res);
        },
        onError: (err) => {
          setError(resolveSendErrorMessage(err, t));
        },
      },
    );
  };

  const resultToast = result ? resolveResultToast(result) : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="send-notification-dialog">
        <DialogHeader>
          <DialogTitle>{t("users.notify.title")}</DialogTitle>
          <DialogDescription>
            {t("users.notify.description", {
              name: user.displayName || user.email,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <fieldset className="space-y-1.5">
            <legend className="font-sans text-sm font-medium text-foreground">
              {t("users.notify.channelLabel")}
            </legend>
            <div className="flex flex-wrap gap-4">
              {CHANNELS.map((c) => {
                const disabled = c === "SMS" && !smsAvailable;
                return (
                  <label
                    key={c}
                    className={
                      disabled ? "flex items-center gap-2 opacity-50" : "flex items-center gap-2"
                    }
                  >
                    <input
                      data-testid={`notification-channel-${c}`}
                      type="radio"
                      name="notification-channel"
                      value={c}
                      checked={channel === c}
                      disabled={disabled}
                      onChange={() => {
                        setChannel(c);
                        clearFeedback();
                      }}
                      className="h-4 w-4"
                    />
                    <span className="font-sans text-sm font-normal text-foreground">
                      {t(`users.notify.channel${c.charAt(0)}${c.slice(1).toLowerCase()}`)}
                    </span>
                  </label>
                );
              })}
            </div>
            {!smsAvailable && (
              <p
                data-testid="notification-sms-disabled-hint"
                className="font-sans text-xs font-normal text-muted-foreground"
              >
                {t("users.notify.smsDisabledHint")}
              </p>
            )}
          </fieldset>

          {channel === "PUSH" && (
            <div className="space-y-1.5">
              <label
                htmlFor="notification-title"
                className="font-sans text-sm font-medium text-foreground"
              >
                {t("users.notify.titleLabel")}
              </label>
              <Input
                id="notification-title"
                data-testid="notification-title-input"
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  clearFeedback();
                }}
                placeholder={t("users.notify.titlePlaceholder")}
              />
            </div>
          )}

          {channel === "EMAIL" && (
            <div className="space-y-1.5">
              <label
                htmlFor="notification-subject"
                className="font-sans text-sm font-medium text-foreground"
              >
                {t("users.notify.subjectLabel")}
              </label>
              <Input
                id="notification-subject"
                data-testid="notification-subject-input"
                type="text"
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  clearFeedback();
                }}
                placeholder={t("users.notify.subjectPlaceholder")}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label
              htmlFor="notification-body"
              className="font-sans text-sm font-medium text-foreground"
            >
              {t("users.notify.bodyLabel")}
            </label>
            <Textarea
              id="notification-body"
              data-testid="notification-body-textarea"
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                clearFeedback();
              }}
              placeholder={t("users.notify.bodyPlaceholder")}
            />
          </div>

          <label className="flex items-start gap-2">
            <input
              data-testid="notification-bypass-checkbox"
              type="checkbox"
              checked={bypassPreferences}
              onChange={(e) => {
                setBypassPreferences(e.target.checked);
                clearFeedback();
              }}
              className="mt-0.5 h-4 w-4"
            />
            <span className="font-sans text-sm font-normal text-foreground">
              {t("users.notify.bypassLabel")}
              <span className="mt-0.5 block font-sans text-xs font-normal text-muted-foreground">
                {t("users.notify.bypassHint")}
              </span>
            </span>
          </label>

          {showPushNoDevice && (
            <Warning
              testId="notification-push-warning"
              text={t("users.notify.pushNoDeviceWarning")}
            />
          )}

          {willSkip && (
            <Warning testId="notification-pref-warning" text={t("users.notify.prefOffWarning")} />
          )}

          {resultToast && (
            <div data-testid="send-notification-result" className={toneClass(resultToast.tone)}>
              {t(resultToast.key, resultToast.values)}
            </div>
          )}

          {error && (
            <p
              data-testid="send-notification-error"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t("users.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-send-notification-button"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {mutation.isPending ? t("users.notify.pending") : t("users.notify.send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toneClass(tone: "success" | "warning" | "error"): string {
  const base = "rounded-md border px-3 py-2 font-sans text-sm font-medium";
  if (tone === "success") {
    return `${base} border-emerald-300/40 bg-emerald-50 text-emerald-800`;
  }
  if (tone === "warning") {
    return `${base} border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200`;
  }
  return `${base} border-destructive/30 bg-destructive/5 text-destructive`;
}

interface WarningProps {
  readonly testId: string;
  readonly text: string;
}

function Warning({ testId, text }: WarningProps) {
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" aria-hidden="true" />
      <p className="font-sans text-xs font-normal text-amber-900 dark:text-amber-200">{text}</p>
    </div>
  );
}
